// ============================================
// SUBSTÂNCIAS CONTROLADAS — Portaria SVS/MS 344/98
// ============================================
// B356 passo 1 (AI_154). Reescrito a partir da auditoria
// `docs/auditoria-lista-344-AI_154.md`. A lista antiga (feita à mão) achatava
// A, B1 e C1 num só grupo e emitia o MESMO receituário branco para todas —
// clonazepam (B1) saía em documento de C1.
//
// Agora:
//   1. As LISTAS vêm do texto oficial (RDC 1.036/2026, Atualização n. 101),
//      via gerador → `portaria344Listas.generated.ts`. Nada de curadoria manual.
//   2. Cada lista aponta o DOCUMENTO que a própria RDC exige no cabeçalho.
//   3. As EXCEÇÕES dos adendos (por dose ou forma) ficam aqui, à mão, cada uma
//      com a citação do adendo. Um teste confere que o trecho citado existe.
//   4. Quando a exceção depende de dose/forma que o nome não informa, o
//      classificador NÃO chuta: devolve uma PERGUNTA ao médico (decisão D2).
//
// Decisões de Leonardo (AI_154): D1 Notificação A/B/B2 → o app bloqueia e manda
// usar o talonário; D2 dose ausente → perguntar; D3 lista completa gerada da
// RDC; D4 sibutramina → bloqueia; D5 (revista) tramadol/codeína em solução →
// Receita de Controle Especial, pela dose por mL.

import {
  LISTAS_344,
  ListaPortaria344,
} from "./portaria344Listas.generated";

export type DocumentoExigido =
  | "notificacaoA" // Notificação de Receita A (amarela) — A1, A2, A3
  | "notificacaoB" // Notificação de Receita B (azul) — B1
  | "notificacaoB2" // Notificação de Receita B2 — B2
  | "notificacaoEspecial" // Notificação de Receita Especial — C2, C3
  | "controleEspecial" // Receita de Controle Especial em 2 vias — C1, C5, exceções
  | "semRetencao"; // prescrição comum (exceções que saem do controle)

export const NOME_DOCUMENTO: Record<DocumentoExigido, string> = {
  notificacaoA: "Notificação de Receita A (amarela)",
  notificacaoB: "Notificação de Receita B (azul)",
  notificacaoB2: "Notificação de Receita B2",
  notificacaoEspecial: "Notificação de Receita Especial",
  controleEspecial: "Receita de Controle Especial (2 vias)",
  semRetencao: "Receita comum",
};

/** Documento que o CABEÇALHO de cada lista exige (texto da RDC). */
const DOCUMENTO_DA_LISTA: Record<ListaPortaria344, DocumentoExigido> = {
  A1: "notificacaoA",
  A2: "notificacaoA",
  A3: "notificacaoA",
  B1: "notificacaoB",
  B2: "notificacaoB2",
  C1: "controleEspecial",
  C2: "notificacaoEspecial",
  C3: "notificacaoEspecial",
  C5: "controleEspecial",
};

/** Documentos que o app NÃO emite hoje (D1): o médico usa o talonário. */
export function exigeTalonario(doc: DocumentoExigido): boolean {
  return (
    doc === "notificacaoA" ||
    doc === "notificacaoB" ||
    doc === "notificacaoB2" ||
    doc === "notificacaoEspecial"
  );
}

// Quanto maior, mais restritivo. Numa associação (ex.: oxicodona + naloxona),
// vale o documento mais restritivo entre os componentes.
const RIGOR: Record<DocumentoExigido, number> = {
  semRetencao: 0,
  controleEspecial: 1,
  notificacaoEspecial: 2,
  notificacaoB: 3,
  notificacaoB2: 4,
  notificacaoA: 5,
};

// D5 (AI_154, REVISTA por Leonardo na validação ao vivo) — solução de substância
// da A2 com exceção "≤ N mg por unidade posológica" (tramadol, codeína...). A
// decisão inicial foi conservadora (Notificação A). A lista CMED/Anvisa registra
// tramadol 100 mg/mL gotas e codeína 3 mg/mL com tarja VERMELHA sob restrição
// (retenção), não preta — o registro trata a solução pela exceção. Leonardo
// decidiu seguir o registro: a dose por mL segue a mesma regra do limite.
// `true` volta ao comportamento conservador (toda solução → Notificação A).
export const SOLUCAO_A2_COMO_NOTIFICACAO_A = false;

// ------------------------------------------------------------
// Normalização e busca por PALAVRA INTEIRA
// ------------------------------------------------------------
export function normalizar(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const escaparRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

interface EntradaLista {
  grafia: string; // normalizada
  lista: ListaPortaria344;
  re: RegExp;
}

// Palavra inteira: "morfina" NÃO casa com "etilmorfina"; "lorazepam" NÃO casa
// com "clorazepam" (armadilhas da busca por pedaço, §3.6 da auditoria).
// Grafias USUAIS na prática/na CMED que diferem da grafia da RDC. Sem isto,
// "Valproato de sódio 500mg" (nome da CMED) passava despercebido, porque a RDC
// escreve "Valproato sódico"; idem "fentanil" × "Fentanila", "meperidina" ×
// "Petidina". Cada apelido aponta a grafia oficial (e herda lista e adendos).
// Isômeros entram pelo adendo 1 de cada lista ("ficam também sob controle os
// sais, éteres, ésteres e isômeros").
export const GRAFIAS_USUAIS: Record<string, string> = {
  "valproato de sodio": "valproato sodico",
  valproato: "valproato sodico",
  fentanil: "fentanila",
  alfentanil: "alfentanila",
  sufentanil: "sufentanila",
  remifentanil: "remifentanila",
  meperidina: "petidina",
  ketamina: "cetamina",
  esketamina: "escetamina",
  levossulpirida: "sulpirida", // isômero (adendo 1 da C1)

  // NOMES COMERCIAIS mais prescritos — cada um conferido na lista CMED/Anvisa
  // (coluna PRODUTO → SUBSTÂNCIA, conformidade 09/09/2026). Sem isto "Rivotril
  // 2mg" saía como receita comum. Fora de propósito: marcas que são palavras
  // comuns (ex.: "Turno", "Lune") e "Androgel" (gel = isento; o nome não diz).
  rivotril: "clonazepam",
  frontal: "alprazolam",
  lexotan: "bromazepam",
  valium: "diazepam",
  lorax: "lorazepam",
  dormonid: "midazolam",
  stilnox: "zolpidem",
  zolfest: "zolpidem",
  prysma: "eszopiclona",
  imovane: "zopiclona",
  ritalina: "metilfenidato",
  venvanse: "lisdexanfetamina",
  gardenal: "fenobarbital",
  hidantal: "fenitoina",
  tramal: "tramadol",
  ultracet: "tramadol",
  tylex: "codeina",
  codein: "codeina",
  oxycontin: "oxicodona",
  targin: "oxicodona",
  restiva: "buprenorfina",
  transtec: "buprenorfina",
  dimorf: "morfina",
  mytedom: "metadona",
  palexis: "tapentadol",
  durogesic: "fentanila",
  zoloft: "sertralina",
  lexapro: "escitalopram",
  pristiq: "desvenlafaxina",
  cymbalta: "duloxetina",
  luvox: "fluvoxamina",
  wellbutrin: "bupropiona",
  zetron: "bupropiona",
  remeron: "mirtazapina",
  donaren: "trazodona",
  brintellix: "vortioxetina",
  valdoxan: "agomelatina",
  amytril: "amitriptilina",
  pamelor: "nortriptilina",
  lyrica: "pregabalina",
  depakote: "divalproato de sodio",
  depakene: "valproato sodico",
  tegretol: "carbamazepina",
  trileptal: "oxcarbazepina",
  keppra: "levetiracetam",
  topamax: "topiramato",
  lamictal: "lamotrigina",
  vimpat: "lacosamida",
  fycompa: "perampanel",
  carbolitium: "litio",
  seroquel: "quetiapina",
  zyprexa: "olanzapina",
  risperdal: "risperidona",
  haldol: "haloperidol",
  rexulti: "brexpiprazol",
  invega: "paliperidona",
  geodon: "ziprasidona",
  ansitec: "buspirona",
  revia: "naltrexona",
  uninaltrex: "naltrexona",
  deposteron: "testosterona",
};

/** Grafia oficial de uma grafia (usual ou já oficial) — base das chaves. */
const oficialDe = (g: string) => GRAFIAS_USUAIS[g] ?? g;

const regexPalavra = (g: string) =>
  new RegExp(`(^|[^a-z0-9])${escaparRegex(g)}([^a-z0-9]|$)`);

const ENTRADAS_OFICIAIS: EntradaLista[] = (
  Object.keys(LISTAS_344) as ListaPortaria344[]
).flatMap((lista) =>
  LISTAS_344[lista].map((g) => {
    const grafia = normalizar(g);
    return { grafia, lista, re: regexPalavra(grafia) };
  })
);

const ENTRADAS: (EntradaLista & { oficial: string })[] = [
  ...ENTRADAS_OFICIAIS.map((e) => ({ ...e, oficial: e.grafia })),
  ...Object.entries(GRAFIAS_USUAIS).flatMap(([apelido, oficial]) => {
    const base = ENTRADAS_OFICIAIS.find((e) => e.grafia === oficial);
    return base ? [{ grafia: apelido, lista: base.lista, re: regexPalavra(apelido), oficial }] : [];
  }),
];

// ------------------------------------------------------------
// Leitura de dose e forma a partir do nome
// ------------------------------------------------------------
export interface Dose {
  mg: number;
  /** Concentração (mg/mL ou mg/g): solução, gotas, injetável, gel. */
  porVolume: boolean;
}

const RE_DOSE =
  /(\d+(?:[.,]\d+)?)\s*(mg|mcg|µg|g)(?:\s*\/\s*\d*(?:[.,]\d+)?\s*(ml|g|h|24 ?h|dose))?(?![a-z])/g;

function paraMg(valor: string, unidade: string): number {
  const v = parseFloat(valor.replace(",", "."));
  if (unidade === "mcg" || unidade === "µg") return v / 1000;
  if (unidade === "g") return v * 1000;
  return v;
}

function lerDoses(nome: string): Dose[] {
  const out: Dose[] = [];
  RE_DOSE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = RE_DOSE.exec(nome))) {
    out.push({
      mg: paraMg(m[1], m[2]),
      porVolume: m[3] === "ml" || m[3] === "g",
    });
  }
  return out;
}

/**
 * Dose da substância `alvo` dentro do nome do medicamento, ou null se não dá
 * para saber com segurança. Regras, na ordem:
 *   1. associação "Paracetamol + Codeína 500mg + 30mg" — alinha por posição
 *      (se o nº de doses não bate com o nº de componentes → null, pergunta);
 *   2. "tramadol 50 mg" — número logo após a substância;
 *   3. uma única dose no nome inteiro.
 */
export function doseDaSubstancia(nomeNorm: string, alvo: string): Dose | null {
  // Associação primeiro: em "Paracetamol + Codeína 500mg + 30mg" o número logo
  // após "codeína" é o do PARACETAMOL — só o alinhamento por posição acerta.
  const doses = lerDoses(nomeNorm);
  if (nomeNorm.includes("+")) {
    const inicioNumeros = nomeNorm.search(/\d/);
    const parteNomes =
      inicioNumeros >= 0 ? nomeNorm.slice(0, inicioNumeros) : nomeNorm;
    if (parteNomes.includes("+")) {
      // Nomes juntos, doses juntas: "A + B 500mg + 30mg" → por posição.
      const componentes = parteNomes.split("+").map((x) => x.trim()).filter(Boolean);
      const idx = componentes.findIndex((c) => c.includes(alvo));
      return idx >= 0 && doses.length === componentes.length ? doses[idx] : null;
    }
    // Cada componente com a sua dose: "Codeína 30mg + Paracetamol 500mg". Se o
    // componente do alvo não tem número ("Paracetamol 750mg + codeína"), não se
    // empresta a dose do vizinho → null → pergunta ao médico.
    const componente = nomeNorm.split("+").find((c) => c.includes(alvo));
    const dosesComp = componente ? lerDoses(componente) : [];
    return dosesComp.length === 1 ? dosesComp[0] : null;
  }
  const logoApos = new RegExp(
    `${escaparRegex(alvo)}[a-z ]{0,20}?\\s*(\\d+(?:[.,]\\d+)?)\\s*(mg|mcg|µg|g)(?:\\s*\\/\\s*(ml|g|h))?(?![a-z])`
  ).exec(nomeNorm);
  if (logoApos) {
    return {
      mg: paraMg(logoApos[1], logoApos[2]),
      porVolume: logoApos[3] === "ml" || logoApos[3] === "g",
    };
  }
  if (doses.length === 1) return doses[0];
  return null;
}

// Marcas que SÓ existem na forma em questão entram na própria regex
// (OxyContin e Targin são sempre LP; Restiva e Transtec são sempre adesivo).
const RE_LIBERACAO_CONTROLADA =
  /liberacao (controlada|prolongada|lenta)|\b(lc|lp|cr|xr|retard|continus|oxycontin|targin)\b/;
const RE_ADESIVO = /adesivo|transderm|patch|mcg ?\/ ?h|\b(restiva|transtec)\b/;
// Forma líquida/injetável escrita por extenso ("tramadol 100mg gotas"): para a
// D5 vale como solução mesmo sem "mg/mL" (achado da revisão adversarial).
const RE_LIQUIDO =
  /\b(gotas?|solucao|xarope|suspensao|injetavel|ampolas?|frasco|elixir|ev|im|iv)\b|\/ ?\d*(,\d+)? ?ml\b/;
const RE_TOPICO =
  /\b(gel|creme|pomada|locao|topic[oa]|uso topico|solucao topica|unguento)\b/;
const RE_FORMAS_BENZIDAMINA =
  /\b(spray|gel|colutorio|pastilha|drops|po para|pasta|ginecologic)/;

// ------------------------------------------------------------
// Exceções dos ADENDOS (texto da RDC 1.036/2026)
// ------------------------------------------------------------
export interface PerguntaAdendo {
  /** Chave estável para guardar a resposta do médico. */
  chave: string;
  texto: string;
  seSim: DocumentoExigido;
  seNao: DocumentoExigido;
}

interface Decisao {
  documento: DocumentoExigido;
  fundamento: string;
  pergunta?: PerguntaAdendo;
}

type RegraAdendo = (
  nomeNorm: string,
  grafia: string,
  resposta: boolean | undefined
) => Decisao | null;

/** Trechos citados — o teste confere que cada um está no texto da RDC. */
export const CITACOES_ADENDOS = {
  A1_oxicodona:
    "comprimidos de liberação controlada à base de oxicodona, contendo não mais que 40 miligramas",
  A1_buprenorfina:
    "adesivos transdérmicos contendo buprenorfina em matriz polimérica adesiva",
  A2_codeina:
    "em que a quantidade de entorpecentes não exceda 100 miligramas por unidade posológica",
  A2_tramadol:
    "em que a quantidade não exceda 100 miligramas de tramadol por unidade posológica",
  A2_nalbufina:
    "em que a quantidade não exceda 10 miligramas de cloridrato de nalbufina por unidade posológica",
  A2_dextropropoxifeno:
    "preparações à base de dextropropoxifeno, inclusive as misturadas a um ou mais componentes, em que a quantidade de entorpecente não exceda 100 miligramas",
  B1_barbituricos:
    "os medicamentos que contenham fenobarbital, metilfenobarbital (prominal), barbital e barbexaclona, ficam sujeitos a prescrição da Receita de Controle Especial",
  B1_perampanel:
    "os medicamentos que contenham perampanel ficam sujeitos à prescrição em Receita de Controle Especial",
  B1_carisoprodol:
    "os medicamentos que contenham carisoprodol estão sujeitos à VENDA SOB PRESCRIÇÃO SEM RETENÇÃO DE RECEITA",
  C1_loperamida:
    "os medicamentos à base da substância loperamida ficam sujeitos a VENDA SOB PRESCRIÇÃO MÉDICA SEM RETENÇÃO DE RECEITA",
  C1_benzidamina:
    "os medicamentos à base de benzidamina cujas formas farmacêuticas sejam: pó para preparação extemporânea, solução ginecológica, spray, pastilha drops, colutório, pasta dentifrícia e gel",
  C2_C5_topicos:
    "os medicamentos de uso tópico contendo as substâncias desta Lista ficam sujeitos a VENDA SOB PRESCRIÇÃO MÉDICA SEM RETENÇÃO DE RECEITA",
} as const;

/** A2: exceção "≤ N mg por unidade posológica" → Receita de Controle Especial. */
function regraDoseA2(
  limiteMg: number,
  citacao: keyof typeof CITACOES_ADENDOS,
  rotulo: string
): RegraAdendo {
  return (nomeNorm, grafia, resposta) => {
    const dose = doseDaSubstancia(nomeNorm, grafia);
    const liquido = RE_LIQUIDO.test(nomeNorm);
    if ((dose && dose.porVolume) || liquido) {
      return SOLUCAO_A2_COMO_NOTIFICACAO_A
        ? {
            documento: "notificacaoA",
            fundamento: `${rotulo} em solução: tratado como Notificação A (decisão D5, conservadora)`,
          }
        : dose
        ? dose.mg <= limiteMg
          ? {
              documento: "controleEspecial",
              fundamento: `${rotulo} ${dose.mg} mg${dose.porVolume ? "/mL" : ""} ≤ ${limiteMg} mg — adendo da lista A2 (solução: conforme registro CMED, decisão D5)`,
            }
          : {
              documento: "notificacaoA",
              fundamento: `${rotulo} ${dose.mg} mg${dose.porVolume ? "/mL" : ""} > ${limiteMg} mg — lista A2`,
            }
        : { documento: "notificacaoA", fundamento: `${rotulo} em solução sem dose informada — lista A2` };
    }
    if (dose) {
      return dose.mg <= limiteMg
        ? {
            documento: "controleEspecial",
            fundamento: `${rotulo} ${dose.mg} mg por unidade ≤ ${limiteMg} mg — adendo da lista A2`,
          }
        : {
            documento: "notificacaoA",
            fundamento: `${rotulo} ${dose.mg} mg por unidade > ${limiteMg} mg — lista A2`,
          };
    }
    const pergunta: PerguntaAdendo = {
      chave: `${oficialDe(grafia)}-dose`,
      texto: `${rotulo}: a dose por unidade (comprimido/cápsula) ou por mL (gotas/solução) é de até ${limiteMg} mg?`,
      seSim: "controleEspecial",
      seNao: "notificacaoA",
    };
    if (resposta === undefined) {
      return { documento: "notificacaoA", fundamento: "dose não informada", pergunta };
    }
    return {
      documento: resposta ? "controleEspecial" : "notificacaoA",
      fundamento: resposta
        ? `${rotulo} ≤ ${limiteMg} mg por unidade (informado pelo médico) — adendo da lista A2`
        : `${rotulo} > ${limiteMg} mg por unidade (informado pelo médico) — lista A2`,
    };
  };
}

const paraControle =
  (fundamento: string): RegraAdendo =>
  () => ({ documento: "controleEspecial", fundamento });
const paraComum =
  (fundamento: string): RegraAdendo =>
  () => ({ documento: "semRetencao", fundamento });

const REGRAS_ADENDO: Record<string, RegraAdendo> = {
  // ---- A2 (adendos 2 a 5) ----
  codeina: regraDoseA2(100, "A2_codeina", "Codeína"),
  diidrocodeina: regraDoseA2(100, "A2_codeina", "Diidrocodeína"),
  acetildiidrocodeina: regraDoseA2(100, "A2_codeina", "Acetildiidrocodeína"),
  etilmorfina: regraDoseA2(100, "A2_codeina", "Etilmorfina"),
  folcodina: regraDoseA2(100, "A2_codeina", "Folcodina"),
  nicodicodina: regraDoseA2(100, "A2_codeina", "Nicodicodina"),
  norcodeina: regraDoseA2(100, "A2_codeina", "Norcodeína"),
  tramadol: regraDoseA2(100, "A2_tramadol", "Tramadol"),
  dextropropoxifeno: regraDoseA2(100, "A2_dextropropoxifeno", "Dextropropoxifeno"),
  nalbufina: regraDoseA2(10, "A2_nalbufina", "Nalbufina"),

  // ---- A1 adendo 5: oxicodona LC ≤ 40 mg ----
  oxicodona: (nomeNorm, grafia, resposta) => {
    const lc = RE_LIBERACAO_CONTROLADA.test(nomeNorm);
    const dose = doseDaSubstancia(nomeNorm, grafia);
    if (lc && dose && !dose.porVolume) {
      return dose.mg <= 40
        ? {
            documento: "controleEspecial",
            fundamento: `Oxicodona liberação controlada ${dose.mg} mg ≤ 40 mg — adendo 5 da lista A1`,
          }
        : { documento: "notificacaoA", fundamento: `Oxicodona ${dose.mg} mg > 40 mg — lista A1` };
    }
    const pergunta: PerguntaAdendo = {
      chave: `${oficialDe(grafia)}-lc40`,
      texto:
        "Oxicodona: é comprimido de LIBERAÇÃO CONTROLADA/PROLONGADA com até 40 mg por comprimido?",
      seSim: "controleEspecial",
      seNao: "notificacaoA",
    };
    if (resposta === undefined) {
      return { documento: "notificacaoA", fundamento: "forma/dose não informadas", pergunta };
    }
    return {
      documento: resposta ? "controleEspecial" : "notificacaoA",
      fundamento: resposta
        ? "Oxicodona LC ≤ 40 mg (informado pelo médico) — adendo 5 da lista A1"
        : "Oxicodona fora da exceção (informado pelo médico) — lista A1",
    };
  },

  // ---- A1 adendo 8: buprenorfina adesivo transdérmico (matriz) ----
  buprenorfina: (nomeNorm, grafia, resposta) => {
    if (RE_ADESIVO.test(nomeNorm)) {
      return {
        documento: "controleEspecial",
        fundamento: "Buprenorfina adesivo transdérmico — adendo 8 da lista A1",
      };
    }
    const pergunta: PerguntaAdendo = {
      chave: `${oficialDe(grafia)}-adesivo`,
      texto: "Buprenorfina: é ADESIVO TRANSDÉRMICO?",
      seSim: "controleEspecial",
      seNao: "notificacaoA",
    };
    if (resposta === undefined) {
      return { documento: "notificacaoA", fundamento: "forma não informada", pergunta };
    }
    return {
      documento: resposta ? "controleEspecial" : "notificacaoA",
      fundamento: resposta
        ? "Buprenorfina adesivo (informado pelo médico) — adendo 8 da lista A1"
        : "Buprenorfina não-adesivo — lista A1",
    };
  },

  // ---- B1 ----
  fenobarbital: paraControle("Fenobarbital — adendo 2 da lista B1"),
  metilfenobarbital: paraControle("Metilfenobarbital — adendo 2 da lista B1"),
  barbital: paraControle("Barbital — adendo 2 da lista B1"),
  barbexaclona: paraControle("Barbexaclona — adendo 2 da lista B1"),
  perampanel: paraControle("Perampanel — adendo 9 da lista B1"),
  carisoprodol: paraComum("Carisoprodol — adendo 16 da lista B1 (sem retenção)"),

  // ---- C1 ----
  loperamida: paraComum("Loperamida — adendo 2 da lista C1 (sem retenção)"),
  benzidamina: (nomeNorm) =>
    RE_FORMAS_BENZIDAMINA.test(nomeNorm)
      ? {
          documento: "semRetencao",
          fundamento: "Benzidamina em forma isenta — adendo 7 da lista C1",
        }
      : { documento: "controleEspecial", fundamento: "Benzidamina — lista C1" },
};

// C2 e C5: formas de uso tópico saem do controle (adendo 2 de cada lista).
function regraTopico(lista: ListaPortaria344, nomeNorm: string): Decisao | null {
  if ((lista === "C2" || lista === "C5") && RE_TOPICO.test(nomeNorm)) {
    return {
      documento: "semRetencao",
      fundamento: `Uso tópico — adendo 2 da lista ${lista} (sem retenção)`,
    };
  }
  return null;
}

// ------------------------------------------------------------
// API pública
// ------------------------------------------------------------
export interface ClassificacaoControlado {
  /** Substância reconhecida (grafia da RDC, normalizada). */
  substancia: string;
  lista: ListaPortaria344;
  documento: DocumentoExigido;
  fundamento: string;
  /** Presente quando a decisão depende de dose/forma não informadas (D2). */
  pergunta?: PerguntaAdendo;
}

/**
 * Classifica um medicamento pelo NOME (como digitado ou vindo do catálogo).
 * Retorna null se nenhuma substância das listas A1–C5 aparecer no nome.
 * `respostas` guarda o que o médico respondeu às perguntas dos adendos,
 * indexado pela `chave` da pergunta.
 */
export function classificarMedicamento(
  nome: string,
  respostas: Record<string, boolean> = {}
): ClassificacaoControlado | null {
  const nomeNorm = normalizar(nome);
  if (nomeNorm.length < 3) return null;

  // Grafias casadas; descarta uma grafia contida numa maior que também casou
  // (ex.: "valproato de sodio" vence "valproato").
  const casadas = ENTRADAS.filter((e) => e.re.test(nomeNorm));
  const achadas = casadas.filter(
    (e) => !casadas.some((o) => o !== e && o.grafia.includes(e.grafia))
  );
  if (achadas.length === 0) return null;

  let melhor: ClassificacaoControlado | null = null;
  for (const e of achadas) {
    // Regras são indexadas pela grafia OFICIAL; a dose é procurada logo após o
    // nome como foi ESCRITO (e.grafia pode ser o apelido usual).
    const regra = REGRAS_ADENDO[e.oficial];
    const chaveResposta = Object.keys(respostas).find((k) =>
      k.startsWith(`${e.oficial}-`)
    );
    const decisao: Decisao = (regra &&
      regra(nomeNorm, e.grafia, chaveResposta ? respostas[chaveResposta] : undefined)) ||
      regraTopico(e.lista, nomeNorm) || {
        documento: DOCUMENTO_DA_LISTA[e.lista],
        fundamento: `Lista ${e.lista} da Portaria 344/98`,
      };
    const c: ClassificacaoControlado = {
      substancia: e.oficial,
      lista: e.lista,
      documento: decisao.documento,
      fundamento: decisao.fundamento,
      pergunta: decisao.pergunta,
    };
    if (
      !melhor ||
      RIGOR[c.documento] > RIGOR[melhor.documento] ||
      (c.pergunta && !melhor.pergunta && RIGOR[c.documento] === RIGOR[melhor.documento])
    ) {
      melhor = c;
    }
  }
  return melhor;
}

/**
 * Compatibilidade: substâncias da prescrição que exigem a Receita de Controle
 * Especial (o documento que o app EMITE). As que exigem Notificação ficam de
 * fora — o Receituário as trata como bloqueio (D1).
 */
export function detectarControlados(
  medicamentos: { nome: string }[],
  respostas: Record<string, boolean> = {}
): string[] {
  const out: string[] = [];
  for (const med of medicamentos) {
    const c = classificarMedicamento(med.nome, respostas);
    if (c && c.documento === "controleEspecial" && !c.pergunta && !out.includes(c.substancia)) {
      out.push(c.substancia);
    }
  }
  return out;
}

/** Compatibilidade com o nome antigo (retorna a substância, qualquer lista). */
export function detectarSubstanciaControlada(nomeMedicamento: string): string | null {
  return classificarMedicamento(nomeMedicamento)?.substancia ?? null;
}
